import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Upload, Loader2, Sparkles, FileText, Image as ImageIcon, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/hooks/useT";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";

const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";
const VISION_COST = 2;

/**
 * OAK Vision — image/PDF upload that OCRs the content via Gemini 3 Flash
 * multimodal, extracts a single actionable learning goal, and offers
 * one-click hand-off into the Counselor (learning path) flow or the
 * Simulation/Lens modal. Cost: 2 credits (bypassed for Limitless).
 */
export default function VisionScan({ onGoalExtracted }) {
  const { lang } = useT();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large — 8 MB max.");
      return;
    }

    setBusy(true);
    try {
      trackEvent("vision_scan_triggered", {
        mime: file.type,
        bytes: file.size,
        language: lang,
      });
      const body = new FormData();
      body.append("file", file);
      body.append("language", lang);
      const { data } = await api.post("/vision/scan", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      trackEvent("vision_scan_succeeded", {
        mime: file.type,
        credits_spent: data?.credits_spent,
        has_goal: Boolean(data?.goal),
      });
      toast.success(`Goal extracted — ${data?.credits_spent || 0} credits used.`);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 402) {
        toast.error("You need at least 2 credits for OAK Vision.");
        try { window.dispatchEvent(new Event("oak:open-upgrade")); } catch {}
      } else {
        toast.error(err?.response?.data?.detail || "Vision failed — try a clearer file.");
      }
      trackEvent("vision_scan_failed", { status: code });
    } finally {
      setBusy(false);
    }
  };

  const useGoal = () => {
    if (!result?.goal) return;
    trackEvent("vision_goal_accepted", { goal_length: result.goal.length });
    if (typeof onGoalExtracted === "function") {
      onGoalExtracted(result);
    } else {
      // Default: drop the goal into the Hub search bar via sessionStorage
      // and navigate home so the existing Counselor flow can pick it up.
      try {
        sessionStorage.setItem("oak_vision_goal", result.goal);
        sessionStorage.setItem("oak_vision_lenses", JSON.stringify(result.suggested_lenses || []));
      } catch {}
      navigate("/");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("oak:vision-goal", { detail: result }));
      }, 120);
    }
  };

  return (
    <section
      className="glass p-5 md:p-6 space-y-4"
      data-testid="vision-scan-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Eye size={11} className="text-[#8aa8ff]" /> OAK VISION
          </div>
          <h3 className="font-display text-xl md:text-2xl tracking-tight mt-1">
            Turn any page or screenshot into a learning goal
          </h3>
          <p className="text-white/55 text-sm mt-1 leading-relaxed">
            Upload a photo, screenshot, or PDF. OAK Vision extracts the text and
            proposes one mastery path plus three simulation lenses.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-full border border-[#FF7B00]/40 bg-[#FF7B00]/10 text-[#ffb067] text-[11px] font-display font-semibold px-2.5 py-1"
          title="Costs 2 credits · free for Limitless VIP"
        >
          <Coins size={11} /> {VISION_COST} credits
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handle}
        className="hidden"
        data-testid="vision-file-input"
      />

      {!result && (
        <button
          onClick={pick}
          disabled={busy}
          className="w-full rounded-2xl border-2 border-dashed border-white/15 hover:border-[#8aa8ff]/60 hover:bg-white/[0.03] transition p-6 md:p-8 flex flex-col items-center justify-center gap-2 text-white/70 hover:text-white disabled:opacity-60"
          data-testid="vision-scan-trigger"
        >
          {busy ? (
            <>
              <Loader2 size={28} className="animate-spin text-[#8aa8ff]" />
              <div className="font-display text-sm mt-1">Scanning…</div>
              <div className="text-xs text-white/45">OAK Vision is reading your file.</div>
            </>
          ) : (
            <>
              <Upload size={28} className="text-[#8aa8ff]" />
              <div className="font-display text-sm mt-1">Click to upload an image or PDF</div>
              <div className="text-xs text-white/45 font-mono-oak">
                PNG · JPG · WEBP · PDF · up to 8 MB
              </div>
            </>
          )}
        </button>
      )}

      {result && (
        <div className="space-y-4" data-testid="vision-scan-result">
          <div className="rounded-2xl border border-[#00FF9D]/30 bg-[#00FF9D]/[0.04] p-4 md:p-5">
            <div className="label-eyebrow flex items-center gap-1.5 !text-[10px]">
              <Sparkles size={11} className="text-[#00FF9D]" /> EXTRACTED GOAL
            </div>
            <div className="font-display text-lg md:text-xl tracking-tight mt-1 text-white">
              {result.goal}
            </div>
            {result.summary && (
              <p className="text-white/65 text-sm mt-2 leading-relaxed">{result.summary}</p>
            )}
            {Array.isArray(result.suggested_lenses) && result.suggested_lenses.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {result.suggested_lenses.map((l) => (
                  <span
                    key={l}
                    className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-mono-oak text-white/75"
                    data-testid={`vision-lens-${l.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l}
                  </span>
                ))}
              </div>
            )}
          </div>

          {result.extracted_text && (
            <details className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <summary className="cursor-pointer text-xs text-white/55 hover:text-white/80 inline-flex items-center gap-1.5">
                {result.mime === "application/pdf" ? <FileText size={11} /> : <ImageIcon size={11} />}
                View extracted text
              </summary>
              <pre className="whitespace-pre-wrap font-mono-oak text-[12px] text-white/65 mt-2 max-h-64 overflow-y-auto leading-relaxed">
                {result.extracted_text}
              </pre>
            </details>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={useGoal}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-semibold btn-primary"
              data-testid="vision-use-goal"
            >
              <Sparkles size={13} /> Generate path for this goal
            </button>
            <button
              onClick={() => { setResult(null); pick(); }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border border-white/15 hover:border-white/30 text-white/75 hover:text-white transition"
              data-testid="vision-scan-another"
            >
              <Upload size={13} /> Scan another
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
